import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TranscriptionSchemaType } from "@/schemas/transcription.schema";
import { Label } from "@radix-ui/react-label";
import { t } from "i18next";
import { useFormContext } from "react-hook-form";

interface Props {
  onChange: (data: Partial<TranscriptionSchemaType>) => void;
  isSubmitting: boolean;
  loading: boolean;
  data: {
    transcriptionLanguages: TranscriptionLanguage[];
    styles: {
      value: string;
      label: string;
    }[];
    formats: {
      value: string;
      label: string;
    }[];
  };
}

export const TranscriptionDefaultSelects = (props: Props): React.ReactNode => {
  const { setValue, getValues } = useFormContext<TranscriptionSchemaType>();

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="style">
          {t("settings.transcription.defaultStyle")}
        </Label>
        <Select
          disabled={props.isSubmitting}
          name="style"
          value={getValues("style")}
          onValueChange={(value) => {
            props.onChange({ style: value });
          }}
        >
          <SelectTrigger id="style">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {props.data.styles.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="format">
          {t("settings.transcription.defaultFormat")}
        </Label>
        <Select
          disabled={props.isSubmitting}
          name="format"
          value={getValues("format")}
          onValueChange={(value) => {
            setValue("format", value);
            props.onChange({ format: value });
          }}
        >
          <SelectTrigger id="format">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {props.data.formats.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="transcriptionLanguage">
          {t("settings.transcription.defaultTranscriptionLanguage")}
        </Label>
        <Select
          disabled={props.isSubmitting || props.loading}
          name="transcriptionLanguage"
          value={getValues("transcriptionLanguage")}
          onValueChange={(value) => {
            const selected = props.data.transcriptionLanguages.find(
              (lang) => lang.name === value
            );
            if (selected) {
              setValue("transcriptionLanguage", value);
              setValue("transcriptionLanguageId", selected.id);
              props.onChange({ transcriptionLanguageId: selected.id });
            }
          }}
        >
          <SelectTrigger id="transcriptionLanguage">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {props.data.transcriptionLanguages.map((lang) => (
              <SelectItem key={lang.id} value={lang.name}>
                {lang.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
